import type {
    TAddOrgExam,
    TFetchOrgQuestionsByExam,
    TValidateOrgExamsWorkerPayload,
    TValidateOrgExamsWorkerResult,
} from '@/store/exam/types'
import type {
    Teach,
    Study,
} from '@pocketprep/types'
import { fetchLibLoadable, fetchLoadable, resetLoadable } from '@/loadingUtils'
import { Parse, runCloudFunction } from '@/store/ParseUtils'
import { examModule } from '@/store/exam/module'
import { instructorModule } from '@/store/instructor/module'
import { groupModule } from '@/store/group/module'
import { organizationModule } from '@/store/organization/module'
import { studentModule } from '@/store/student/module'
import { userModule } from '@/store/user/module'
import ValidateOrgExamsWorker from '@/store/exam/workers/validateOrgExamsWorker?worker'

const fetchYourOrgExamIds = async () => {
    const instructorsPromise = instructorModule.actions.fetchCurrentOrgInstructors()
    const groupsPromise = groupModule.actions.fetchCurrentOrgGroups()

    await Promise.all([
        instructorsPromise,
        groupsPromise,
    ])
}

const addOrgExam = async (params: {
    organizationId: string
    examMetadataId: string
    displayName: string
}): Promise<Teach.Class.OrgExamJSON> => {
    const createdOrgExam = await runCloudFunction<TAddOrgExam>('addOrgExam', params)

    resetLoadable(examModule.state.currentOrgExams[params.organizationId])
    await fetchCurrentOrgExams()
    userModule.actions.updateIntercom()

    return createdOrgExam
}

const fetchExamMetadata = async () => {
    const examMetadataQuery = new Parse.Query<Study.Class.ExamMetadata>('ExamMetadata')

    return fetchLoadable(examModule.state.examMetadata, async () => {
        const exams = await examMetadataQuery.findAll({ batchSize: 10000 })
        return exams.reduce(
            (acc, e) => ({ ...acc, [e.id]: e.toJSON() }),
            {} as { [key: string]: Study.Class.ExamMetadataJSON }
        )
    })
}

const fetchCurrentOrgExams = async () => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (!currentOrganization) {
        return {}
    }

    return fetchLibLoadable(examModule, 'currentOrgExams', currentOrganization.objectId, async () => {
        type TFetchOrgExams = (params: { organizationId: string }) => Teach.Class.OrgExamJSON[]
        const orgExams = await runCloudFunction<TFetchOrgExams>(
            'fetchOrgExams',
            { organizationId: currentOrganization.objectId }
        )
        return orgExams.reduce(
            (acc, e) => ({ ...acc, [e.objectId]: e }),
            {} as { [key: string]: Teach.Class.OrgExamJSON }
        )
    })
}

const fetchCurrentStudentExams = async () => {
    const currentStudent = await studentModule.actions.fetchCurrentStudent()

    if (!currentStudent) {
        return []
    }

    // Using the groups guarantees that the list of exams will only be ones that this instructor has access to
    const orgExamsAvailableToInstructorIds = examModule.getters.getYourOrgExamIds()
    const currentOrgGroups = Object.values(groupModule.getters.getCurrentOrgGroups())

    const allStudentOrgExamIds = currentOrgGroups.filter(
        group => group.orgStudents.find(orgStudent => orgStudent.objectId === currentStudent.objectId)
            && orgExamsAvailableToInstructorIds.includes(group.orgExam.objectId)
    ).map(group => group.orgExam.objectId)

    const studentOrgExamIds = [ ...new Set(allStudentOrgExamIds) ]

    const currentStudentId = currentStudent.objectId

    const currentOrgExams = await fetchCurrentOrgExams()

    return fetchLibLoadable(examModule, 'currentStudentExams', currentStudentId, async () =>
        Promise.resolve(Object.values(currentOrgExams).filter(e => studentOrgExamIds.includes(e.objectId)))
    )
}

const fetchCurrentStudentCurrentExam = async () => {
    if (examModule.state.currentStudentCurrentExamId) {
        return examModule.getters.getCurrentStudentCurrentExam()
    } else {
        const currentGroup = await groupModule.actions.fetchCurrentGroup()
        const studentExams = await fetchCurrentStudentExams()
        const currentStudentExam = studentExams.find(exam => exam.objectId === currentGroup?.orgExam.objectId)

        if (currentStudentExam && examModule.state.currentStudentCurrentExamId !== currentStudentExam.objectId) {
            examModule.state.currentStudentCurrentExamId = currentStudentExam.objectId
        }

        return currentStudentExam
    }
}

const fetchCurrentGroupExam = async () => {
    const orgExamsDispatch = fetchCurrentOrgExams()
    const currentGroupDispatch = groupModule.actions.fetchCurrentGroup()


    await Promise.all([
        orgExamsDispatch,
        currentGroupDispatch,
    ])

    return examModule.getters.getCurrentGroupExam()
}

const fetchQuestionsByExam = async (params: {
    compositeKey: string
    organizationId: string
    examMetadataId: string
}, forceFetch?: boolean
) => {
    return fetchLoadable(examModule.state.currentExamQuestions, async () => {
        return runCloudFunction<TFetchOrgQuestionsByExam>('fetchOrgQuestionsByExam', {
            ...params,
            includeMatrixQuestions: true,
            includeBuildListQuestions: true,
            includeMPMCQuestions: true,
        })
    }, forceFetch)
    // Pass along the forceFetch arg whenever a function passes it in (mostly for force reloading questions
    // when swapping exams)
}

const fetchSubjectsWithLevels = async () => {
    const currentExamMetadata = examModule.getters.getCurrentExamMetadata()
    if (!currentExamMetadata) {
        throw new Error('fetchSubjectsWithLevels: Unable to get current exam metadata')
    }
    return fetchLibLoadable(examModule, 'examSubjectsWithLevels', currentExamMetadata.objectId, async () =>
        await runCloudFunction<Study.Cloud.fetchSubjectsWithLevels>('fetchSubjectsWithLevels', {
            examGuid: currentExamMetadata.examGuid,
            majorVersion: currentExamMetadata.version.split('.')[0] || '',
            examMetadataId: currentExamMetadata.objectId,
        }))
}

const updateCurrentExamMetadataId = async (examMetadataId: string) => {
    // Set the "current" exam by metadataId
    const previousExamMetadata = examModule.getters.getCurrentGroupExam()

    const currentOrganization = organizationModule.getters.getCurrentOrganization()
    if (previousExamMetadata && currentOrganization && previousExamMetadata.objectId !== examMetadataId) {
        // Need to swap exams
        const examMetadata = examModule.getters.getExamMetadata()[examMetadataId]
        const examGuid = examMetadata?.examGuid
        if (!examGuid) {
            return
        }

        // Update the examGuid's current version
        examModule.state.currentExamMetadataIdByExamGuid[examGuid] = examMetadata.objectId

        // set lastFetched to null
        resetLoadable(examModule.state.currentExamQuestions)
    }
    return Promise.resolve()
}

const validateOrgExams = async (): Promise<null> => {
    try {
        // prevent running multiple validations at once
        return await fetchLoadable(examModule.state.validateOrgExams, async () => {
            await doValidateOrgExams()
            return null
        })
    } finally {
        resetLoadable(examModule.state.validateOrgExams)
    }
}

const doValidateOrgExams = async () => {
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    const organizationId = currentOrganization?.objectId
    if (!organizationId) {
        return
    }

    const currentOrgExams = await examModule.actions.fetchCurrentOrgExams()
    const guidOrgExamLib = examModule.getters.getGuidOrgExamLib()
    const guidExamMetadataLib = examModule.getters.getGuidExamMetadataLib()

    const orgExams = Object.values(currentOrgExams)
    const examGuidsForOrgExams = Object.keys(guidOrgExamLib)
    const examMetadataIdsInOrgExamsSet = new Set(orgExams.map(orgExam => orgExam.examMetadata.objectId))

    const workerPayload: TValidateOrgExamsWorkerPayload = JSON.parse(JSON.stringify({
        guidExamMetadataLib,
        examGuidsForOrgExams,
        examMetadataIdsInOrgExamsList: Array.from(examMetadataIdsInOrgExamsSet),
        organizationId,
    }))
    const validateOrgExamsWorker = new ValidateOrgExamsWorker()
    validateOrgExamsWorker.postMessage(workerPayload)
    validateOrgExamsWorker.onmessage = async (event: MessageEvent<TValidateOrgExamsWorkerResult>) => {
        const { payloadsForMissingOrgExams } = event.data
        await Promise.all(payloadsForMissingOrgExams.map(payload => examModule.actions.addOrgExam(payload)))
    }

}

const fetchMockExams = async () => {
    return fetchLoadable(examModule.state.mockExams, async () => {
        return runCloudFunction<Study.Cloud.fetchMockExams>('fetchMockExams')
    })
}

export default {
    fetchYourOrgExamIds,
    addOrgExam,
    fetchExamMetadata,
    fetchCurrentOrgExams,
    fetchCurrentStudentExams,
    fetchCurrentStudentCurrentExam,
    fetchCurrentGroupExam,
    fetchQuestionsByExam,
    fetchSubjectsWithLevels,
    updateCurrentExamMetadataId,
    validateOrgExams,
    fetchMockExams,
}
